/* eslint-disable no-param-reassign */
import { useNavigate } from "react-router-dom";
import { useCallback, useContext, useEffect, useState } from "react";
import { Box, IconButton, SxProps, TableRow, Typography, Tooltip, SpeedDial } from "@mui/material";
import { SuccessAlertContext } from "App";
import CloseIcon from "@mui/icons-material/Close";
import { checkAccessTokenExpired } from "util/auth";
import ERROR_MESSAGE from "constants/message";
import Notify from "config/notify";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import {
  AmountHiddenSymbol,
  Category,
  CompanySearchApiParams,
  CompanyStock,
  CompanyStockSet,
  CompanyStockSingle,
  CompanyStockSingleInSet,
} from "../types";
import StockTable from "../components/StockTable";
import StockSearchDialog from "../components/StockSearchDialog";
import StockContainer from "../components/StockContainer";
import useStockSearch from "../hooks/useStockSearch";
import TableCellSmall, {
  MultiTypography,
  TableCellNarrow,
  TableCellWide,
  TableHeaderCellSmall,
} from "../components/TableCellSmall";
import { categories, supplierBackgroundColor, supplierHeaderColor } from "../constants";
import useCompanyStockList from "../hooks/useCompanyStockList";
import { extractNoDupplicationSupplier, localeStringDisZero, removeEmptyValues } from "../utils/common";
import CustomTooltip from "../components/CustomTooltip";

const limit = 10;
const targetShopLengthLimit = 10; // 対象店舗の最大選択数

const styles: { [key: string]: SxProps } = {
  supplierTopCol: {
    textAlign: "center",
    p: 0.5,
    borderBottom: "none",
  },
  supplierBottomCol: {
    textAlign: "center",
    p: 0.5,
    top: 33,
  },
};

// 隠すことのできるカラム名
const toggleVisibleColumns = [
  "商品コード",
  "JANコード",
  "大分類",
  "仕入先名",
  "ブランド",
  "商品名",
  "品番",
  "サイズ",
];
// 取り除くカラムを設定(タイヤ・ホイールの場合は空)
const generalInvisibleColumnIndices: number[] = [1, 2, 4, 7];
const tyreWheelInvisibleColumnIndices: number[] = [1, 2, 6];
const defaultInvisibleColumnIndices = (c: Category) =>
  c === categories[1] ? generalInvisibleColumnIndices : tyreWheelInvisibleColumnIndices;

/**
 * 企業用在庫一覧画面
 * @returns JSX.Element
 */
export default function CompanyStockList() {
  const isExistCd = localStorage.getItem("company_cd");
  const { showAlert } = useContext(SuccessAlertContext);
  const [isLoading, setIsLoading] = useState(false);
  const [
    {
      total,
      stockList,
      isLoading: isStockLoading,
      isMoreLoading: isMoreStockLoading,
      isFastLoading,
      fireMoreLoadEvent,
      storeUpdated,
      storehouseUpdated,
    },
    { get: getStockList, getMore: getMoreStockList, setFireMoreLoadEvent, setInit },
  ] = useCompanyStockList(showAlert);
  const [isOpenStockSearch, setIsOpenStockSearch] = useState(true);
  const [
    {
      productCd,
      productName,
      brand,
      JANCd,
      productNumber,
      tyreWidth,
      tyreRatio,
      tyreRimDiam,
      wheelWidth,
      wheelPcd,
      wheelHole,
      wheelRimDiam,
      classification,
      category,
      supplier,
    },
    {
      setProductCd,
      setProductName,
      setBrand,
      setJANCd,
      setProductNumber,
      setTyreWidth,
      setTyreRatio,
      setTyreRimDiam,
      setWheelWidth,
      setWheelPcd,
      setWheelHole,
      setWheelRimDiam,
      setClassification,
      setCategory,
      setSupplier,
    },
    { classificationOptions, tyreOptions, wheelOptions, brandOptions, shopOptions },
    { isLoading: isOptionsLoading },
  ] = useStockSearch(true);
  const [filteredCategory, setFilteredCategory] = useState<Category>(category);
  const [targetShopCdList, setTargetShopCdList] = useState<string[]>([]);
  const [filteredTargetShopCdList, setFilteredTargetShopCdList] = useState<string[]>([]);

  const navigate = useNavigate();
  const [invisibleColumnIndices, setInvisibleColumnIndices] = useState<number[]>(
    defaultInvisibleColumnIndices(category),
  );

  const [isScrollTop, setIsScrollTop] = useState(false);
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // 単品かセットか
  const checkProductSet = useCallback(
    (stock: CompanyStock): stock is CompanyStockSet => "productSetCd" in stock,
    [],
  );

  // 現在の検索条件にあるデータを基にAPIのパラメータを作成
  const createGetListApiParams = (): CompanySearchApiParams => {
    const params: Omit<CompanySearchApiParams, "limit" | "offset" | "shop_cds"> = {
      item_cd: productCd,
      item_name: productName,
      brand: brand ?? "",
      jan_cd: JANCd,
      item_num: productNumber,
      tire_width: classification === "タイヤ" ? tyreWidth : undefined,
      tire_ratio: classification === "タイヤ" ? tyreRatio : undefined,
      tire_rim_size: classification === "タイヤ" ? tyreRimDiam : undefined,
      wheel_width: classification === "ホイール" ? wheelWidth : undefined,
      wheel_pcd: classification === "ホイール" ? wheelPcd : undefined,
      wheel_hole: classification === "ホイール" ? wheelHole : undefined,
      wheel_rim_size: classification === "ホイール" ? wheelRimDiam : undefined,
      class_cd: classificationOptions.some((co) => co.name === classification)
        ? classificationOptions.filter((co) => co.name === classification)[0].id
        : "",
      vendor_cd: supplier
        ? extractNoDupplicationSupplier(classificationOptions).filter(
            (so) => so.supplierName === supplier,
          )[0].supplierCd
        : "",
    };
    return { ...removeEmptyValues(params), shop_cds: targetShopCdList };
  };

  const onSearchClick = async () => {
    setIsOpenStockSearch(false);
    await getStockList(createGetListApiParams());
    // 表示するカラムのリセット
    setInvisibleColumnIndices(defaultInvisibleColumnIndices(category));
    setFilteredCategory(category);
    setFilteredTargetShopCdList(targetShopCdList);
  };

  const handleToggleOpenColumn = (index: number) => {
    setInvisibleColumnIndices((prev) => {
      const newIndices = [...prev];
      if (!prev.includes(index)) {
        newIndices.push(index);
        return newIndices;
      }
      return prev;
    });
  };

  const handleVisibleColumn = useCallback((index: number) => {
    setInvisibleColumnIndices((prev) => {
      const newIndices = [...prev];
      newIndices.splice(index, 1);
      return newIndices;
    });
  }, []);

  const isCategoryGeneral = useCallback(
    () => filteredCategory === categories[1],
    [filteredCategory],
  );

  const handleBack = () => navigate("/");

  const checkHiddenSupplier = (
    sDate: string,
    amounts: {
      centerAmount: number | AmountHiddenSymbol;
      neighborAmount: number | AmountHiddenSymbol;
    }[],
  ) => {
    if (!sDate || amounts.every((a) => a.neighborAmount === "-" && a.centerAmount === "-"))
      return "-";
    return sDate;
  };

  // 対象の店舗を抽出
  const getTargetShopList = useCallback(
    () => filteredTargetShopCdList.map((tsCd) => shopOptions.filter((so) => so.shopCd === tsCd)[0]),
    [filteredTargetShopCdList, shopOptions],
  );

  // 単品のセル
  const clickableSingleCell = useCallback(
    (stock: CompanyStockSingle, show: boolean | undefined) =>
      show ? (
        <TableRow key={stock.id}>
          {[
            <TableCellSmall key="product_cd">
              {stock.isDiscontinued ? (
                <CustomTooltip title="*がついているものは廃番です。" arrow>
                  <Box sx={{ cursor: "pointer" }}>
                    <Typography component="span" sx={{ color: "red" }}>
                      *
                    </Typography>
                    {stock.productCd}
                  </Box>
                </CustomTooltip>
              ) : (
                stock.productCd
              )}
            </TableCellSmall>,
            <TableCellSmall key="jan_cd">{stock.JANCd}</TableCellSmall>,
            <TableCellWide key="classification">
              <MultiTypography>
                {classificationOptions.filter((c) => c.id === stock.classification).length > 0
                  ? classificationOptions.filter((c) => c.id === stock.classification)[0].name
                  : stock.classification}
              </MultiTypography>
            </TableCellWide>,
            <TableCellWide key="supplier_name" sx={{ backgroundColor: supplierBackgroundColor }}>
              <MultiTypography>{stock.supplierName}</MultiTypography>
            </TableCellWide>,
            <TableCellSmall key="brand">
              <MultiTypography>{stock.brand ?? ""}</MultiTypography>
            </TableCellSmall>,
            <TableCellWide key="product_name">
              <MultiTypography>{stock.productName ? stock.productName : null}</MultiTypography>
            </TableCellWide>,
            <TableCellSmall key="product_number">{stock.productNumber}</TableCellSmall>,
            <TableCellSmall key="size">{stock.size ?? null}</TableCellSmall>,
          ].filter((_, index) => !invisibleColumnIndices.includes(index))}
          {stock.storeAmountList.map((amount) => (
            <TableCellSmall key={amount.storeCd} sx={{ textAlign: "right" }}>
              {amount.isStagnantStock ? (
                <CustomTooltip title="※がついているものは滞留在庫です。" arrow>
                  <Box sx={{ cursor: "pointer" }}>
                    <Typography component="span" sx={{ color: "red", fontSize: 14 }}>
                      ※
                    </Typography>
                    {amount.storeAmount.toLocaleString()}
                  </Box>
                </CustomTooltip>
              ) : (
                amount.storeAmount.toLocaleString()
              )}
            </TableCellSmall>
          ))}
          <TableCellSmall sx={{ textAlign: "right" }}>
            {localeStringDisZero(stock.tyreStorehouse)}
          </TableCellSmall>
          {stock.storeAmountList.map((amount) => (
            <>
              {!isCategoryGeneral() && (
                <TableCellSmall
                  key={`neighbor_${amount.storeCd}`}
                  sx={{ textAlign: "right", backgroundColor: supplierBackgroundColor }}
                >
                  {amount.neighborAmount.toLocaleString()}
                </TableCellSmall>
              )}
              <TableCellSmall
                key={`center_${amount.storeCd}`}
                sx={{ textAlign: "right", backgroundColor: supplierBackgroundColor }}
              >
                {amount.centerAmount.toLocaleString()}
              </TableCellSmall>
            </>
          ))}
          <TableCellSmall sx={{ backgroundColor: supplierBackgroundColor }}>
            {checkHiddenSupplier(stock.arrivalDate, stock.storeAmountList)}
          </TableCellSmall>
          <TableCellNarrow sx={{ backgroundColor: supplierBackgroundColor, maxWidth: 60 }}>
            {checkHiddenSupplier(stock.updateTime, stock.storeAmountList)}
          </TableCellNarrow>
        </TableRow>
      ) : (
        // eslint-disable-next-line react/jsx-no-useless-fragment
        <></>
      ),
    [invisibleColumnIndices, isCategoryGeneral],
  );

  // 混合のセル
  const fieldCell = useCallback(
    (stock: CompanyStock) => {
      let ele: JSX.Element;
      if (checkProductSet(stock)) {
        // セット
        ele = (
          <TableRow key={stock.id}>
            {[
              <TableCellSmall key="product_cd">
                <Box display="flex" alignItems="center">
                  {stock.isDiscontinued ? (
                    <CustomTooltip title="*がついているものは廃番です。" arrow>
                      <Box sx={{ cursor: "pointer" }}>
                        <Typography component="span" sx={{ color: "red" }}>
                          *
                        </Typography>
                        {stock.productSetCd}
                      </Box>
                    </CustomTooltip>
                  ) : (
                    stock.productSetCd
                  )}
                </Box>
              </TableCellSmall>,
              <TableCellSmall key="jan_cd">
                {(() => (
                  <>
                    <Box marginBottom={1}>
                      <MultiTypography>{stock.JANCd.split('+')[0]}</MultiTypography>
                    </Box>
                    <Box>
                      <MultiTypography>{stock.JANCd.split('+')[1]}</MultiTypography>
                    </Box>
                  </>
                ))()}
              </TableCellSmall>,
              <TableCellSmall key="classification">
                {classificationOptions.filter((c) => c.id === stock.classification).length > 0
                  ? classificationOptions.filter((c) => c.id === stock.classification)[0].name
                  : stock.classification}
              </TableCellSmall>,
              <TableCellSmall key="supplier_name" sx={{ backgroundColor: supplierBackgroundColor }}>
                -
              </TableCellSmall>,
              <TableCellSmall key="brand">
                <Box marginBottom={1}>
                  <MultiTypography>{stock.stockSingleList[0].brand}</MultiTypography>
                </Box>
                <Box>
                  <MultiTypography>{stock.stockSingleList[1].brand}</MultiTypography>
                </Box>
              </TableCellSmall>,
              <TableCellSmall key="product_name">
                <Box marginBottom={1}>
                  <MultiTypography>{stock.stockSingleList[0].productName}</MultiTypography>
                </Box>
                <Box>
                  <MultiTypography>{stock.stockSingleList[1].productName}</MultiTypography>
                </Box>
              </TableCellSmall>,
              <TableCellSmall key="product_number">
                {(() => (
                  <>
                    <Box marginBottom={1}>
                      <MultiTypography>{stock.productNumber.split('+')[0]}</MultiTypography>
                    </Box>
                    <Box>
                      <MultiTypography>{stock.productNumber.split('+')[1]}</MultiTypography>
                    </Box>
                  </>
                ))()}
                <Box display="flex" alignItems="center">
                  {stock.productNumber}
                </Box>
              </TableCellSmall>,
              <TableCellSmall key="size">
                <Box marginBottom={1}>
                  <MultiTypography>{stock.stockSingleList[0].size}</MultiTypography>
                </Box>
                <Box>
                  <MultiTypography>{stock.stockSingleList[1].size}</MultiTypography>
                </Box>
              </TableCellSmall>,
            ].filter((_, index) => !invisibleColumnIndices.includes(index))}
            {stock.storeAmountList.map((amount) => (
              <TableCellSmall key={amount.storeCd} sx={{ textAlign: "right" }}>
                {amount.isStagnantStock ? (
                  <CustomTooltip title="※がついているものは滞留在庫です。" arrow>
                    <Box sx={{ cursor: "pointer" }}>
                      <Typography component="span" sx={{ color: "red", fontSize: 14 }}>
                        ※
                      </Typography>
                      {amount.storeAmount.toLocaleString()}
                    </Box>
                  </CustomTooltip>
                ) : (
                  amount.storeAmount.toLocaleString()
                )}
              </TableCellSmall>
            ))}
            <TableCellSmall sx={{ textAlign: "right" }}>
              {localeStringDisZero(stock.tyreStorehouse)}
            </TableCellSmall>
            {stock.storeAmountList.map((amount) => (
              <>
                {!isCategoryGeneral() && (
                  <TableCellSmall
                    key={`neighbor_${amount.storeCd}`}
                    sx={{ textAlign: "right", backgroundColor: supplierBackgroundColor }}
                  >
                    -
                  </TableCellSmall>
                )}
                <TableCellSmall
                  key={`center_${amount.storeCd}`}
                  sx={{ textAlign: "right", backgroundColor: supplierBackgroundColor }}
                >
                  -
                </TableCellSmall>
              </>
            ))}
            <TableCellSmall sx={{ backgroundColor: supplierBackgroundColor }}>-</TableCellSmall>
            <TableCellNarrow sx={{ backgroundColor: supplierBackgroundColor, maxWidth: 60 }}>
              -
            </TableCellNarrow>
          </TableRow>
        );
      } else {
        // 単品
        ele = clickableSingleCell(stock, true);
      }
      return ele;
    },
    [isCategoryGeneral, clickableSingleCell, checkProductSet, invisibleColumnIndices],
  );

  const iconButton = (index: number, colName: string) => (
    <Box width="100%">
      <Box width="fit-content" position="relative">
        {colName}
        <Box
          position="absolute"
          top={-20}
          right={10}
          width={0}
          height="fit-content"
          alignContent="center"
        >
          <IconButton onClick={() => handleToggleOpenColumn(index)}>
            <CloseIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );

  const tableHeadElement = useCallback(
    () => (
      <>
        <TableRow>
          {[
            <TableHeaderCellSmall key="product_cd" rowSpan={2}>
              商品コード
            </TableHeaderCellSmall>,
            <TableHeaderCellSmall key="jan_cd" rowSpan={2}>
              {iconButton(1, "JANコード")}
            </TableHeaderCellSmall>,
            <TableHeaderCellSmall key="classification" rowSpan={2}>
              {iconButton(2, "大分類")}
            </TableHeaderCellSmall>,
            <TableHeaderCellSmall
              key="supplier_name"
              rowSpan={2}
              sx={{ backgroundColor: supplierHeaderColor }}
            >
              {iconButton(3, "仕入先名")}
            </TableHeaderCellSmall>,
            <TableHeaderCellSmall key="brand" rowSpan={2}>
              {iconButton(4, "ブランド")}
            </TableHeaderCellSmall>,
            <TableHeaderCellSmall key="product_name" rowSpan={2}>
              {iconButton(5, "商品名")}
            </TableHeaderCellSmall>,
            <TableHeaderCellSmall key="product_number" rowSpan={2}>
              {iconButton(6, "品番")}
            </TableHeaderCellSmall>,
            <TableHeaderCellSmall key="size" rowSpan={2}>
              {iconButton(7, "サイズ")}
            </TableHeaderCellSmall>,
          ].filter((_, index) => !invisibleColumnIndices.includes(index))}
          {getTargetShopList().map((s) => (
            <TableHeaderCellSmall
              key={s.shopCd}
              sx={{ ...styles.supplierTopCol, py: 0 }}
              rowSpan={2}
            >
              <MultiTypography sx={{ textAlign: "center" }}>
                <>
                  {s.shopName}
                  <br />
                  数量
                </>
              </MultiTypography>
            </TableHeaderCellSmall>
          ))}
          {isCategoryGeneral() ? (
            // distributions.map((d) => (
            //   <TableHeaderCellSmall rowSpan={2} sx={{py: 0}}><MultiTypography sx={{textAlign: 'center'}}><>{d.distributionName}<br />数量</></MultiTypography></TableHeaderCellSmall>
            // ))
            <TableHeaderCellSmall rowSpan={2} sx={{ py: 0 }}>
              <MultiTypography sx={{ textAlign: "center" }}>
                <>
                  物流センター
                  <br />
                  数量
                </>
              </MultiTypography>
            </TableHeaderCellSmall>
          ) : (
            <TableHeaderCellSmall rowSpan={2}>
              <MultiTypography sx={{ textAlign: "center" }}>
                <>
                  タイヤYH
                  <br />
                  倉庫数量
                </>
              </MultiTypography>
            </TableHeaderCellSmall>
          )}
          <TableHeaderCellSmall
            colSpan={
              isCategoryGeneral()
                ? 2 + getTargetShopList().length
                : 2 + getTargetShopList().length * 2
            }
            sx={{ backgroundColor: supplierHeaderColor, ...styles.supplierTopCol }}
          >
            仕入先
          </TableHeaderCellSmall>
        </TableRow>
        <TableRow>
          {getTargetShopList().map((s) => (
            <>
              {!isCategoryGeneral() && (
                <TableHeaderCellSmall
                  key={`neighbor_${s.shopCd}`}
                  sx={{ ...styles.supplierBottomCol, backgroundColor: supplierHeaderColor, py: 0 }}
                >
                  <MultiTypography sx={{ textAlign: "center" }}>
                    <>
                      {s.shopName}
                      <br />
                      近接倉庫数量
                    </>
                  </MultiTypography>
                </TableHeaderCellSmall>
              )}
              <TableHeaderCellSmall
                key={`center_${s.shopCd}`}
                sx={{ ...styles.supplierBottomCol, backgroundColor: supplierHeaderColor, py: 0 }}
              >
                <MultiTypography sx={{ textAlign: "center" }}>
                  <>
                    {s.shopName}
                    <br />
                    センター倉庫数量
                  </>
                </MultiTypography>
              </TableHeaderCellSmall>
            </>
          ))}
          <TableHeaderCellSmall
            sx={{ ...styles.supplierBottomCol, backgroundColor: supplierHeaderColor }}
          >
            入荷予定日
          </TableHeaderCellSmall>
          <TableHeaderCellSmall
            sx={{ ...styles.supplierBottomCol, backgroundColor: supplierHeaderColor }}
          >
            更新日時
          </TableHeaderCellSmall>
        </TableRow>
      </>
    ),
    [getTargetShopList, isCategoryGeneral, invisibleColumnIndices],
  );

  const tableBodyElement = useCallback(
    () => (
      // eslint-disable-next-line react/jsx-no-useless-fragment
      <>{stockList.length > 0 && stockList.map((stock) => fieldCell(stock))}</>
    ),
    [stockList, fieldCell],
  );

  // スクロール処理
  const handleScroll = async () => {
    setIsScrollTop(window.pageYOffset > 0);

    // ロード中は処理を行わない。stateはaddEventListenerで登録しているので効かず、代わりにrefを使う。
    if (isFastLoading.current) {
      return;
    }
    const scrollTop = window.scrollY;
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;
    if (scrollTop + windowHeight + 10 >= documentHeight) {
      setFireMoreLoadEvent((prev) => !prev);
    }
  };

  const checkScrollable = () => document.documentElement.scrollHeight > window.innerHeight;

  // 中のコンテンツが小さいとスクロールできず、無限ローディングさせることができないため、事前に+αで読んでおく。
  useEffect(() => {
    if (
      limit <= stockList.length &&
      stockList.length < total &&
      !isLoading &&
      !isFastLoading.current
    ) {
      if (!checkScrollable()) {
        (async () => {
          await getMoreStockList(createGetListApiParams());
        })();
      }
    }
  }, [stockList, isLoading]);

  useEffect(() => {
    setTimeout(() => {
      if (!isExistCd) {
        setIsOpenStockSearch(false);
        Notify?.setNotification?.({
          show: true,
          title: ERROR_MESSAGE.UNAUTHORIZED.TITLE,
          message: "企業コードが設定されていません。再度ログインしてください。",
        });
        Notify.onClose = () => {
          localStorage.clear();
          window.location.href = "/login";
        };
      } else if (checkAccessTokenExpired()) {
        // 期限切れなら
        setIsOpenStockSearch(false);
        Notify?.setNotification?.({
          show: true,
          title: ERROR_MESSAGE.UNAUTHORIZED.TITLE,
          message: ERROR_MESSAGE.UNAUTHORIZED.MESSAGE,
        });
        Notify.onClose = () => {
          localStorage.clear();
          window.location.href = "/login";
        };
      }
    }, 0);
  }, []);

  useEffect(() => {
    // addEventListenerするとstateの値を変更しても内部の値は変わらないため、stateを変更させるだけにし、ここで処理を行う。
    (async () => {
      await getMoreStockList(createGetListApiParams());
    })();
  }, [fireMoreLoadEvent]);

  useEffect(() => {
    (async () => {
      setInit(true);
    })();
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    // loadingの統一
    setIsLoading(isOptionsLoading || isStockLoading);
  }, [isOptionsLoading, isStockLoading]);

  useEffect(() => {
    // 対象店舗初期化
    const targetShop = shopOptions.slice(0, 10).map((so) => so.shopCd);
    setTargetShopCdList(targetShop);
  }, [shopOptions]);

  return (
    <Box>
      <StockContainer
        stockTitle="在庫情報一覧"
        StockSearch={
          <StockSearchDialog
            open={isOpenStockSearch}
            onClose={() => setIsOpenStockSearch(false)}
            onSearch={onSearchClick}
            productCd={productCd}
            productName={productName}
            brand={brand}
            JANCd={JANCd}
            productNumber={productNumber}
            tyreWidth={tyreWidth}
            tyreRatio={tyreRatio}
            tyreRimDiam={tyreRimDiam}
            wheelWidth={wheelWidth}
            wheelPcd={wheelPcd}
            wheelHole={wheelHole}
            wheelRimDiam={wheelRimDiam}
            classification={classification}
            supplier={supplier}
            targetShopCdList={targetShopCdList}
            targetShopLengthLimit={targetShopLengthLimit}
            classificationOptions={classificationOptions}
            tyreOptions={tyreOptions}
            wheelOptions={wheelOptions}
            brandOptions={brandOptions}
            shopOptions={shopOptions}
            // supplierOptions={supplierOptions}
            setProductCd={setProductCd}
            setProductName={setProductName}
            setBrand={setBrand}
            setJANCd={setJANCd}
            setProductNumber={setProductNumber}
            setTyreWidth={setTyreWidth}
            setTyreRatio={setTyreRatio}
            setTyreRimDiam={setTyreRimDiam}
            setWheelWidth={setWheelWidth}
            setWheelPcd={setWheelPcd}
            setWheelHole={setWheelHole}
            setWheelRimDiam={setWheelRimDiam}
            setClassification={setClassification}
            setCategory={setCategory}
            setSupplier={setSupplier}
            setTargetShopCdList={setTargetShopCdList}
            isCompanyScreen
          />
        }
        StockTable={
          <StockTable
            searchLen={total}
            tableHeadElement={tableHeadElement}
            tableBodyElement={tableBodyElement}
            isAllLoaded={stockList.length >= total}
            isLoading={isMoreStockLoading}
            storeUpdated={storeUpdated}
            storehouseUpdated={storehouseUpdated}
            toggleVisibleColumns={toggleVisibleColumns}
            invisibleColumnIndices={invisibleColumnIndices}
            handleVisibleColumn={handleVisibleColumn}
          />
        }
        setIsOpenStockSearch={setIsOpenStockSearch}
        isLoading={isLoading}
        onBack={handleBack}
        manualName="manual_company"
      />
      {isScrollTop && (
        <Box
          sx={{
            position: "fixed",
            bottom: 16,
            right: 16,
            zIndex: (theme) => theme.zIndex.speedDial,
          }}
        >
          <SpeedDial
            ariaLabel="Back to top"
            icon={<KeyboardArrowUpIcon />}
            onClick={scrollToTop}
            open // 常に展開済みにしてアイコンだけ表示
            direction="up" // 展開方向は上だがActionは不要
            FabProps={{ size: "small" }}
          />
        </Box>
      )}
    </Box>
  );
}
